import React, { useState, useEffect } from 'react';
import API_URL from './config';

// Convert 24-hour format to 12-hour format
function formatTime12h(time24) {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function getTimeSlots() {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hour = h.toString().padStart(2, '0');
      const min = m.toString().padStart(2, '0');
      slots.push(`${hour}:${min}`);
    }
  }
  return slots;
}

const timeSlots = getTimeSlots();

// Get filtered time slots based on day of week
// Mon-Fri: 3:00 PM (15:00) to 10:00 PM (22:00)
// Sat-Sun: 7:00 AM (07:00) to 10:00 PM (22:00)
function getFilteredTimeSlots(dateStr, showAll) {
  if (showAll) return timeSlots;
  
  const date = new Date(dateStr + 'T12:00:00'); // Use noon to avoid timezone issues
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
  
  return timeSlots.filter(slot => {
    const [hours] = slot.split(':').map(Number);
    if (isWeekend) {
      // Saturday/Sunday: 7:00 AM to 10:00 PM (07:00 to 21:30)
      return hours >= 7 && hours < 22;
    } else {
      // Monday-Friday: 3:00 PM to 10:00 PM (15:00 to 21:30)
      return hours >= 15 && hours < 22;
    }
  });
}

const BOOKING_TYPES = [
  { value: 'full_turf', label: 'Full Turf' },
  { value: 'half_turf', label: 'Half Turf' },
  { value: 'cage_rental', label: 'Cage Rental' },
];

// Helper to get local date string (YYYY-MM-DD) without timezone issues
function getLocalDateString(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function Schedule({ token, refreshKey, onBookingChange, isAdmin, initialDate }) {
  const today = new Date();
  const twoYears = new Date();
  twoYears.setFullYear(today.getFullYear() + 2);
  const todayStr = getLocalDateString(today);
  const twoYearsStr = getLocalDateString(twoYears);
  const [selectedDate, setSelectedDate] = useState(initialDate || todayStr);
  
  // Update selectedDate when initialDate prop changes
  useEffect(() => {
    if (initialDate) {
      setSelectedDate(initialDate);
    }
  }, [initialDate]);
  const [bookings, setBookings] = useState([]);
  const [myPendingBookings, setMyPendingBookings] = useState([]);
  const [allPendingBookings, setAllPendingBookings] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [bookingType, setBookingType] = useState('full_turf');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [blackouts, setBlackouts] = useState([]);
  const [facilityTimezone, setFacilityTimezone] = useState('America/New_York');
  
  // Recurring booking state
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  
  // Time slot display state
  const [showAllSlots, setShowAllSlots] = useState(false);
  
  // Pricing state
  const [priceInfo, setPriceInfo] = useState(null);
  const [recurringPriceInfo, setRecurringPriceInfo] = useState(null);
  
  // Custom rate state (admin only)
  const [customRate, setCustomRate] = useState('');
  const [showCustomRate, setShowCustomRate] = useState(false);

  // Fetch facility timezone setting
  useEffect(() => {
    fetch(`${API_URL}/api/settings/timezone`)
      .then(res => res.json())
      .then(data => {
        if (data.value) setFacilityTimezone(data.value);
      })
      .catch(() => {});
  }, []);

  // Helper function to generate recurring dates
  function generateRecurringDates(startDate, endDate, type) {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    let current = new Date(start);
    
    while (current <= end) {
      dates.push(current.toISOString().slice(0, 10));
      
      switch (type) {
        case 'weekly':
          current.setDate(current.getDate() + 7);
          break;
        case 'biweekly':
          current.setDate(current.getDate() + 14);
          break;
        case 'monthly':
          current.setMonth(current.getMonth() + 1);
          break;
        default:
          current.setDate(current.getDate() + 7);
      }
    }
    return dates;
  }

  // Fetch users list for admin
  useEffect(() => {
    if (!isAdmin) return;
    fetch(`${API_URL}/api/users`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, [isAdmin, token]);

  // Fetch approved bookings for selected date (visible to all)
  useEffect(() => {
    if (!selectedDate) return;
    fetch(`${API_URL}/api/bookings/${selectedDate}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setBookings(Array.isArray(data) ? data : []))
      .catch(() => setBookings([]));
  }, [selectedDate, token, message, refreshKey]);

  // Fetch user's own pending bookings for selected date
  useEffect(() => {
    if (!selectedDate) return;
    fetch(`${API_URL}/api/my-bookings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          // Filter to pending bookings for the selected date
          const pending = data.filter(b => 
            b.status === 'pending' && 
            b.booking_date.slice(0, 10) === selectedDate
          );
          setMyPendingBookings(pending);
        } else {
          setMyPendingBookings([]);
        }
      })
      .catch(() => setMyPendingBookings([]));
  }, [selectedDate, token, message, refreshKey]);

  // Admin: Fetch all pending bookings for selected date
  useEffect(() => {
    if (!isAdmin || !selectedDate) {
      setAllPendingBookings([]);
      return;
    }
    fetch(`${API_URL}/api/admin/bookings`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          // Filter to pending bookings for the selected date
          const pending = data.filter(b => 
            b.status === 'pending' && 
            b.booking_date.slice(0, 10) === selectedDate
          );
          setAllPendingBookings(pending);
        } else {
          setAllPendingBookings([]);
        }
      })
      .catch(() => setAllPendingBookings([]));
  }, [isAdmin, selectedDate, token, message, refreshKey]);

  // Fetch blackouts for selected date
  useEffect(() => {
    if (!selectedDate) return;
    fetch(`${API_URL}/api/blackouts/date/${selectedDate}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setBlackouts(Array.isArray(data) ? data : []))
      .catch(() => setBlackouts([]));
  }, [selectedDate, token, refreshKey]);

  // Fetch pricing when booking type, date, or selected slots change
  useEffect(() => {
    if (!selectedDate || selectedSlots.length === 0) {
      setPriceInfo(null);
      return;
    }
    
    // Extract hours from selected slots (e.g., "08:00" -> 8, "14:30" -> 14)
    const slotHours = selectedSlots.map(slot => parseInt(slot.split(':')[0]));
    const slotTimesParam = slotHours.join(',');
    
    fetch(`${API_URL}/api/pricing/calculate?booking_type=${bookingType}&date=${selectedDate}&slot_times=${slotTimesParam}`)
      .then(res => res.json())
      .then(data => {
        if (data.total_price) {
          setPriceInfo(data);
        } else {
          setPriceInfo(null);
        }
      })
      .catch(() => setPriceInfo(null));
  }, [selectedDate, bookingType, selectedSlots]);

  // Calculate recurring booking total price
  useEffect(() => {
    if (!isRecurring || !recurrenceEndDate || !selectedDate || selectedSlots.length === 0) {
      setRecurringPriceInfo(null);
      return;
    }
    
    // Validate end date is within 2 years to prevent excessive computation
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);
    const endDateObj = new Date(recurrenceEndDate);
    if (endDateObj > maxDate) {
      setRecurringPriceInfo(null);
      return;
    }
    
    const dates = generateRecurringDates(selectedDate, recurrenceEndDate, recurrenceType);
    if (dates.length <= 1) {
      setRecurringPriceInfo(null);
      return;
    }
    
    // Calculate hours based on selected slots
    const hoursPerSession = selectedSlots.length * 0.5; // Each slot is 30 min
    
    // If custom rate is set, use it directly instead of API
    if (showCustomRate && customRate) {
      const rate = parseFloat(customRate);
      const totalHours = hoursPerSession * dates.length;
      const totalPrice = rate * totalHours;
      setRecurringPriceInfo({
        dates: dates.map(date => ({ date, price: rate * hoursPerSession, hours: hoursPerSession })),
        totalDates: dates.length,
        totalPrice: totalPrice.toFixed(2),
        totalHours
      });
      return;
    }
    
    // Extract hours from selected slots
    const slotHours = selectedSlots.map(slot => parseInt(slot.split(':')[0]));
    const slotTimesParam = slotHours.join(',');
    
    // Fetch price for each date
    Promise.all(
      dates.map(date =>
        fetch(`${API_URL}/api/pricing/calculate?booking_type=${bookingType}&date=${date}&slot_times=${slotTimesParam}`)
          .then(res => res.json())
          .then(data => ({
            date,
            price: data.total_price ? parseFloat(data.total_price) : 0,
            hours: data.total_hours || 0
          }))
          .catch(() => ({ date, price: 0, hours: 0 }))
      )
    ).then(results => {
      const totalPrice = results.reduce((sum, r) => sum + r.price, 0);
      const totalHours = results.reduce((sum, r) => sum + r.hours, 0);
      setRecurringPriceInfo({
        dates: results,
        totalDates: dates.length,
        totalPrice: totalPrice.toFixed(2),
        totalHours
      });
    });
  }, [isRecurring, recurrenceEndDate, recurrenceType, selectedDate, bookingType, selectedSlots, showCustomRate, customRate]);

  // Determine availability for each slot
  function getSlotAvailability(slot) {
    const slotBookings = bookings.filter(b => b.time_slot === slot);
    const fullTurf = slotBookings.filter(b => b.booking_type === 'full_turf').length;
    const halfTurf = slotBookings.filter(b => b.booking_type === 'half_turf').length;
    const cage = slotBookings.filter(b => b.booking_type === 'cage_rental').length;
    
    // Full turf requires empty slot
    const canFullTurf = fullTurf === 0 && halfTurf === 0 && cage === 0;
    // Half turf: max 2 if no cages, max 1 if cages exist
    const canHalfTurf = fullTurf === 0 && (cage === 0 ? halfTurf < 2 : halfTurf < 1);
    // Cage rental: max 3, only allowed if half turf <= 1
    const canCage = fullTurf === 0 && halfTurf <= 1 && cage < 3;
    const isBooked = slotBookings.length > 0;
    const isFullyBooked = isBooked && !canHalfTurf && !canCage;
    const isPartial = isBooked && (canHalfTurf || canCage);
    
    return {
      full_turf: canFullTurf,
      half_turf: canHalfTurf,
      cage_rental: canCage,
      booked: isBooked,
      fullyBooked: isFullyBooked,
      partial: isPartial,
      details: slotBookings
    };
  }

  // Check if a slot has pending bookings (admin view)
  function getSlotPendingInfo(slot) {
    if (!isAdmin) return { hasPending: false, hasConflict: false, count: 0 };
    const slotPending = allPendingBookings.filter(b => b.time_slot === slot);
    const uniqueUsers = new Set(slotPending.map(b => b.user_id));
    
    // Check for capacity conflicts (pending + approved exceeds max)
    const pendingByType = {
      full_turf: slotPending.filter(b => b.booking_type === 'full_turf').length,
      half_turf: slotPending.filter(b => b.booking_type === 'half_turf').length,
      cage_rental: slotPending.filter(b => b.booking_type === 'cage_rental').length
    };
    
    // Get current approved counts
    const slotBookings = bookings.filter(b => b.time_slot === slot);
    const approvedCage = slotBookings.filter(b => b.booking_type === 'cage_rental').length;
    const approvedHalf = slotBookings.filter(b => b.booking_type === 'half_turf').length;
    
    // Check for capacity conflicts - only when bookings of a type exceed what can be approved
    let hasCapacityConflict = false;
    
    // Full turf conflict: any pending full turf when slot has bookings, or multiple full turf requests
    if (pendingByType.full_turf > 0 && (slotBookings.length > 0 || pendingByType.half_turf > 0 || pendingByType.cage_rental > 0)) {
      hasCapacityConflict = true;
    }
    if (pendingByType.full_turf > 1) {
      hasCapacityConflict = true;
    }
    
    // Calculate totals
    const totalCages = approvedCage + pendingByType.cage_rental;
    const totalHalf = approvedHalf + pendingByType.half_turf;
    
    // Half turf conflict: if cages exist, max 1 half turf; otherwise max 2
    if (totalCages > 0 && totalHalf > 1) {
      hasCapacityConflict = true;
    }
    if (totalCages === 0 && totalHalf > 2) {
      hasCapacityConflict = true;
    }
    
    // Cage rental conflict: max 3, and only if half turf <= 1
    if (totalCages > 3) {
      hasCapacityConflict = true;
    }
    if (pendingByType.cage_rental > 0 && totalHalf > 1) {
      hasCapacityConflict = true;
    }
    
    return {
      hasPending: slotPending.length > 0,
      hasConflict: hasCapacityConflict,
      count: slotPending.length,
      users: uniqueUsers.size
    };
  }

  // Determine if a slot is more than 30 minutes in the past (applies to EVERYONE including admin)
  function isSlotTooOld(slot) {
    // Get current time in facility timezone
    const now = new Date();
    try {
      const options = { timeZone: facilityTimezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };
      const formatter = new Intl.DateTimeFormat('en-CA', options);
      const parts = formatter.formatToParts(now);
      const dateParts = {};
      parts.forEach(p => dateParts[p.type] = p.value);
      const todayStr = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
      const currentHour = parseInt(dateParts.hour);
      const currentMinute = parseInt(dateParts.minute);
      
      // Past if selected date is before today in facility timezone
      if (selectedDate < todayStr) return true;
      // Future if selected date is after today in facility timezone
      if (selectedDate > todayStr) return false;
      
      // Same day: compare time - slot is too old if started more than 30 minutes ago
      const [slotHour, slotMin] = slot.split(':').map(Number);
      const slotTime = slotHour * 60 + slotMin;
      const currentTime = currentHour * 60 + currentMinute;
      return slotTime < (currentTime - 30);
    } catch (e) {
      // Fallback to local time if timezone conversion fails
      const todayStr = now.getFullYear() + '-' + 
        String(now.getMonth() + 1).padStart(2, '0') + '-' + 
        String(now.getDate()).padStart(2, '0');
      if (selectedDate < todayStr) return true;
      if (selectedDate > todayStr) return false;
      const [slotHour, slotMin] = slot.split(':').map(Number);
      const slotTime = slotHour * 60 + slotMin;
      const currentTime = now.getHours() * 60 + now.getMinutes();
      return slotTime < (currentTime - 30);
    }
  }



  // Check if a slot is blocked by a blackout
  function isSlotBlackedOut(slot) {
    if (blackouts.length === 0) return false;
    for (const b of blackouts) {
      // If blackout has no specific times, it's all day
      if (!b.start_time && !b.end_time) {
        return true;
      }
      // Check if slot falls within blackout time range
      if (b.start_time && b.end_time) {
        if (slot >= b.start_time && slot < b.end_time) {
          return true;
        }
      }
    }
    return false;
  }

  // Get blackout info for tooltip
  function getBlackoutInfo(slot) {
    for (const b of blackouts) {
      if (!b.start_time && !b.end_time) {
        return b.title + (b.reason ? `: ${b.reason}` : '');
      }
      if (b.start_time && b.end_time && slot >= b.start_time && slot < b.end_time) {
        return b.title + (b.reason ? `: ${b.reason}` : '');
      }
    }
    return 'Blocked';
  }

  // Helper to check if selected slots are consecutive
  function areSlotsConsecutive(slots) {
    if (slots.length <= 1) return true;
    const sortedSlots = [...slots].sort();
    for (let i = 1; i < sortedSlots.length; i++) {
      const [prevH, prevM] = sortedSlots[i - 1].split(':').map(Number);
      const [currH, currM] = sortedSlots[i].split(':').map(Number);
      const prevMinutes = prevH * 60 + prevM;
      const currMinutes = currH * 60 + currM;
      if (currMinutes - prevMinutes !== 30) return false;
    }
    return true;
  }

  function toggleSlot(slot) {
    if (selectedSlots.includes(slot)) {
      setSelectedSlots(selectedSlots.filter(s => s !== slot));
    } else {
      setSelectedSlots([...selectedSlots, slot]);
    }
  }

  async function handleBook() {
    setMessage('');
    setError('');
    if (selectedSlots.length === 0) {
      setError('Please select at least one time slot');
      return;
    }
    
    // Non-admin users must book at least 2 consecutive slots
    if (!isAdmin) {
      if (selectedSlots.length < 2) {
        setError('Minimum booking is 2 consecutive time slots (1 hour)');
        return;
      }
      if (!areSlotsConsecutive(selectedSlots)) {
        setError('Time slots must be consecutive');
        return;
      }
    }
    
    if (isAdmin && !selectedUserId) {
      setError('Please select a user to assign the booking to');
      return;
    }
    if (isRecurring && !recurrenceEndDate) {
      setError('Please select an end date for recurring bookings');
      return;
    }
    
    // Validate booking date is within 2 years
    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() + 2);
    const selectedDateObj = new Date(selectedDate);
    if (selectedDateObj > maxDate) {
      setError('Booking date must be within 2 years from today');
      return;
    }
    
    // Validate recurring end date is within 2 years
    if (isRecurring) {
      const recurrenceEndDateObj = new Date(recurrenceEndDate);
      if (recurrenceEndDateObj > maxDate) {
        setError('Recurring end date must be within 2 years from today');
        return;
      }
    }
    
    try {
      // Handle recurring bookings
      if (isRecurring) {
        const res = await fetch(`${API_URL}/api/bookings/recurring`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            booking_date: selectedDate,
            time_slots: selectedSlots,
            booking_type: bookingType,
            auto_approve: isAdmin,
            assign_user_id: isAdmin ? selectedUserId : null,
            custom_rate: isAdmin && customRate ? customRate : null,
            recurrence: {
              type: recurrenceType,
              end_date: recurrenceEndDate
            }
          })
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || 'Recurring booking failed');
        } else {
          // Count unique dates to get number of grouped bookings
          const uniqueDates = new Set(data.bookings.map(b => b.booking_date)).size;
          let msg = isAdmin 
            ? `${uniqueDates} recurring booking(s) added to schedule.`
            : `${uniqueDates} recurring booking request(s) submitted! Awaiting admin approval.`;
          if (data.conflicts && data.conflicts.length > 0) {
            const conflictDates = new Set(data.conflicts.map(c => c.date)).size;
            msg += ` (${conflictDates} date(s) skipped due to conflicts)`;
          }
          if (data.duplicates && data.duplicates.length > 0) {
            const duplicateDates = new Set(data.duplicates.map(d => d.date)).size;
            msg += ` (${duplicateDates} skipped - you already have pending requests for those slots)`;
          }
          setMessage(msg);
          if (onBookingChange) onBookingChange();
        }
        setSelectedSlots([]);
        setIsRecurring(false);
        setRecurrenceEndDate('');
        setCustomRate('');
        setShowCustomRate(false);
        return;
      }
      
      // Handle single/multi-slot bookings using batch endpoint
      const res = await fetch(`${API_URL}/api/bookings/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          booking_date: selectedDate,
          time_slots: selectedSlots,
          booking_type: bookingType,
          auto_approve: isAdmin,
          assign_user_id: isAdmin ? selectedUserId : null,
          custom_rate: isAdmin && customRate ? customRate : null
        })
      });
      const data = await res.json();
      
      if (!res.ok) {
        setError(data.error || 'Booking failed');
      } else {
        // Count as 1 booking since all slots on same date are grouped
        const succeeded = data.bookings && data.bookings.length > 0 ? 1 : 0;
        if (succeeded > 0) {
          setMessage(isAdmin 
            ? `Booking added to schedule.`
            : `Booking request submitted! Awaiting admin approval.`);
          if (onBookingChange) onBookingChange();
        }
        if (data.errors && data.errors.length > 0) {
          setError(`Failed slots: ${data.errors.map(e => `${e.slot} (${e.error})`).join(', ')}`);
        }      }
      setSelectedSlots([]);
      setCustomRate('');
      setShowCustomRate(false);
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="schedule-container">
      <h2>Schedule</h2>
      <div className="date-selector-row">
        <label>
          Select date:
          <input
            type="date"
            min={todayStr}
            max={twoYearsStr}
            value={selectedDate}
            onChange={e => { setSelectedDate(e.target.value); setSelectedSlots([]); }}
          />
        </label>
        {(() => {
          const selectedDateObj = new Date(selectedDate + 'T12:00:00');
          const todayDate = new Date();
          todayDate.setHours(0, 0, 0, 0);
          const maxDate = new Date();
          maxDate.setFullYear(maxDate.getFullYear() + 2);
          return selectedDateObj >= todayDate && selectedDateObj <= maxDate;
        })() && (
          <label className="show-all-slots-toggle">
            <input
              type="checkbox"
              checked={showAllSlots}
              onChange={e => setShowAllSlots(e.target.checked)}
            />
            Display All Time Slots
          </label>
        )}
      </div>
      <div className="timeslots">
        {(() => {
          const selectedDateObj = new Date(selectedDate + 'T12:00:00');
          const todayDate = new Date();
          todayDate.setHours(0, 0, 0, 0);
          const maxDate = new Date();
          maxDate.setFullYear(maxDate.getFullYear() + 2);
          const isDateValid = selectedDateObj >= todayDate && selectedDateObj <= maxDate;
          
          if (!isDateValid) {
            return (
              <div className="invalid-date-message">
                Please select a valid date (today through {maxDate.toLocaleDateString()})
              </div>
            );
          }
          
          return (
            <div className="timeslot-grid">
              {getFilteredTimeSlots(selectedDate, showAllSlots).map(slot => {
            const avail = getSlotAvailability(slot);
            const isSelected = selectedSlots.includes(slot);
            const isPending = myPendingBookings.some(b => b.time_slot === slot);
            const pendingInfo = getSlotPendingInfo(slot);
            const availableTypes = [
              avail.half_turf && 'Half Turf',
              avail.cage_rental && 'Cage Rental'
            ].filter(Boolean).join(', ');
            
            // Determine the appropriate class
            let statusClass = '';
            const blackoutInfo = getBlackoutInfo(slot);
            
            // Blackouts take highest priority
            if (isSlotBlackedOut(slot)) {
              statusClass = ' blackout';
            // Past slots (30+ min old) - applies to everyone including admin
            } else if (isSlotTooOld(slot)) {
              statusClass = ' past';
            } else if (avail.fullyBooked && !pendingInfo.hasPending) {
              // Slot is fully booked with no pending requests
              statusClass = ' booked';
            } else if (avail.fullyBooked && pendingInfo.hasPending) {
              // Slot is fully booked but has pending requests that can't be approved - conflict
              statusClass = ' conflict';
            } else if (pendingInfo.hasConflict) {
              // Pending requests exceed available capacity - conflict
              statusClass = ' conflict';
            } else if (avail.partial) {
              // Slot has some approved bookings but still has availability
              statusClass = ' partial';
            } else if (pendingInfo.hasPending) {
              statusClass = ' pending';
            } else if (isPending) {
              statusClass = ' pending';
            }
            
            // Build tooltip
            let tooltip = 'Available';
            if (isSlotBlackedOut(slot)) {
              tooltip = `Blocked: ${blackoutInfo?.title || 'Facility Closed'}${blackoutInfo?.reason ? ` - ${blackoutInfo.reason}` : ''}`;
            } else if (isSlotTooOld(slot)) {
              tooltip = 'Past — cannot book more than 30 min after slot start';
            } else if (avail.fullyBooked && !pendingInfo.hasPending) {
              tooltip = 'Fully Booked';
            } else if (avail.fullyBooked && pendingInfo.hasPending) {
              tooltip = `Conflict: Fully booked but ${pendingInfo.count} pending request(s) cannot be approved`;
            } else if (pendingInfo.hasConflict) {
              tooltip = `Conflict: ${pendingInfo.count} pending request(s) exceed available capacity`;
            } else if (avail.partial) {
              tooltip = pendingInfo.hasPending
                ? `Partial: ${availableTypes} available (${pendingInfo.count} pending)`
                : `Available: ${availableTypes}`;
            } else if (pendingInfo.hasPending) {
              tooltip = `${pendingInfo.count} pending request(s)`;
            } else if (isPending) {
              tooltip = 'Pending Approval';
            }
            
            return (
              <button
                key={slot}
                className={`timeslot-btn${isSelected ? ' selected' : ''}${statusClass}`}
                onClick={() => toggleSlot(slot)}
                disabled={isSlotBlackedOut(slot) || isSlotTooOld(slot)}
                title={tooltip}
              >
                {formatTime12h(slot)}
              </button>
            );
          })}
            </div>
          );
        })()}
      </div>
      <div className="legend">
        <span className="legend-item">
          <span className="legend-color available"></span>
          Open
        </span>
        <span className="legend-item">
          <span className="legend-color pending"></span>
          Pending
        </span>
        <span className="legend-item">
          <span className="legend-color partial"></span>
          Partial
        </span>
        <span className="legend-item">
          <span className="legend-color booked"></span>
          Booked
        </span>
        <span className="legend-item">
          <span className="legend-color blackout"></span>
          Blocked
        </span>
        {isAdmin && (
          <span className="legend-item">
            <span className="legend-color conflict"></span>
            Conflict
          </span>
        )}
      </div>
      
      {/* Warning for non-admin users about minimum slots */}
      {!isAdmin && selectedSlots.length > 0 && selectedSlots.length < 2 && (
        <div className="min-slots-warning">
          ⚠️ Minimum booking is 2 consecutive time slots (1 hour). Please select {2 - selectedSlots.length} more slot(s).
        </div>
      )}
      {!isAdmin && selectedSlots.length >= 2 && !areSlotsConsecutive(selectedSlots) && (
        <div className="min-slots-warning error">
          ⚠️ Time slots must be consecutive. Please adjust your selection.
        </div>
      )}
      
      {selectedSlots.length > 0 && (
        <div className="booking-form">
          <h4>Book {selectedSlots.length} slot(s) on {selectedDate}</h4>
          <p><strong>Selected:</strong> {selectedSlots.sort().map(s => formatTime12h(s)).join(', ')}</p>
          {isAdmin && (
            <div className="admin-booking-row">
              <label>
                Assign to User:
                <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
                  <option value="">-- Select User --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username} {u.organization ? `(${u.organization})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={showCustomRate}
                  onChange={e => {
                    setShowCustomRate(e.target.checked);
                    if (!e.target.checked) setCustomRate('');
                  }}
                />
                Custom Rate
              </label>
            </div>
          )}
          {isAdmin && showCustomRate && (
            <div className="custom-rate-input">
              <label>
                Rate ($/hr):
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={customRate}
                  onChange={e => setCustomRate(e.target.value)}
                />
              </label>
            </div>
          )}
          <label>
            Booking Type:
            <select value={bookingType} onChange={e => setBookingType(e.target.value)}>
              {BOOKING_TYPES.map(bt => (
                <option key={bt.value} value={bt.value}>
                  {bt.label}
                </option>
              ))}
            </select>
          </label>
          
          {/* Price display */}
          {(priceInfo || (showCustomRate && customRate)) && (
            <div className="price-display">
              {showCustomRate && customRate ? (
                <>
                  <div className="price-info">
                    <span className="price-label">Custom Rate:</span>
                    <span className="price-amount">${parseFloat(customRate).toFixed(2)}/hour</span>
                  </div>
                  {selectedSlots.length > 0 && (
                    <div className="price-total">
                      <span className="price-label">Estimated Total:</span>
                      <span className="price-amount">${(parseFloat(customRate) * selectedSlots.length * 0.5).toFixed(2)}</span>
                      <span className="price-duration">({selectedSlots.length * 0.5} hour{selectedSlots.length * 0.5 !== 1 ? 's' : ''})</span>
                    </div>
                  )}
                </>
              ) : priceInfo && (
                <>
                  {priceInfo.breakdown && priceInfo.breakdown.length > 0 && (
                    <>
                      {priceInfo.has_multiple_rates ? (
                        <div className="price-breakdown">
                          <span className="price-label">Pricing Breakdown:</span>
                          {priceInfo.breakdown.map((item, idx) => (
                            <div key={idx} className="price-breakdown-item">
                              <span className="breakdown-name">{item.rule_name || 'Standard Rate'}</span>
                              <span className="breakdown-rate">${item.price_per_hour}/hr × {item.slots * 0.5} hr</span>
                              <span className="breakdown-subtotal">${item.subtotal.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="price-info">
                          <span className="price-label">Price:</span>
                          <span className="price-amount">${priceInfo.breakdown[0].price_per_hour}/hour</span>
                          {priceInfo.breakdown[0].rule_name && (
                            <span className="price-rule-name">({priceInfo.breakdown[0].rule_name})</span>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {selectedSlots.length > 0 && (
                    <div className="price-total">
                      <span className="price-label">Estimated Total:</span>
                      <span className="price-amount">${priceInfo.total_price}</span>
                      <span className="price-duration">({priceInfo.total_hours} hour{priceInfo.total_hours !== 1 ? 's' : ''})</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* Recurring booking options */}
          <div className="recurring-options">
            <label className="recurring-checkbox">
              <input
                type="checkbox"
                checked={isRecurring}
                onChange={e => setIsRecurring(e.target.checked)}
              />
              Make this a recurring booking
            </label>
            
            {isRecurring && (
              <div className="recurring-settings">
                <label>
                  Repeat:
                  <select value={recurrenceType} onChange={e => setRecurrenceType(e.target.value)}>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Every 2 Weeks</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
                <label>
                  Until:
                  <input
                    type="date"
                    min={selectedDate}
                    max={twoYears.toISOString().slice(0,10)}
                    value={recurrenceEndDate}
                    onChange={e => setRecurrenceEndDate(e.target.value)}
                  />
                </label>
              </div>
            )}
            
            {/* Recurring total cost display */}
            {isRecurring && recurringPriceInfo && (
              <div className="recurring-price-summary">
                <div className="recurring-price-total">
                  <span className="price-label">Recurring Total ({recurringPriceInfo.totalDates} sessions):</span>
                  <span className="price-amount recurring-amount">${recurringPriceInfo.totalPrice}</span>
                  <span className="price-duration">({recurringPriceInfo.totalHours} hour{recurringPriceInfo.totalHours !== 1 ? 's' : ''} total)</span>
                </div>
              </div>
            )}
          </div>
          
          <button onClick={handleBook}>{isAdmin ? 'Book Time Slot' : 'Submit Request'}</button>
        </div>
      )}
      {message && <div className="success">{message}</div>}
      {error && <div className="error">{error}</div>}
    </div>
  );
}

export default Schedule;
